import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function Testimonials() {
  const { t } = useI18n();
  const items = t.testimonials.items;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 6500);
    return () => clearInterval(id);
  }, [items.length]);

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section data-testid="testimonials-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="glow-blob bg-[#EC008C] w-[420px] h-[420px] -bottom-32 -left-24 opacity-25" />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FF1F87]">
          {t.testimonials.eyebrow}
        </span>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
          {t.testimonials.title}
        </h2>

        <div className="mt-14 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              data-testid={`testimonial-${idx}`}
              className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-10 lg:p-14 relative"
            >
              <Quote className="absolute top-6 left-6 w-10 h-10 text-[#EC008C]/30" />
              <div className="flex justify-center gap-1 text-[#FF1F87] mb-6">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <p className="text-xl lg:text-2xl text-zinc-100 font-light leading-relaxed max-w-3xl mx-auto">
                &ldquo;{items[idx].q}&rdquo;
              </p>
              <div className="mt-8">
                <div className="font-display font-bold text-white">{items[idx].n}</div>
                <div className="text-xs text-zinc-400 mt-1">{items[idx].c}</div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-center gap-3">
            <button
              data-testid="testimonial-prev"
              onClick={prev}
              className="w-10 h-10 rounded-full border border-white/15 hover:border-[#EC008C] hover:bg-[#EC008C]/10 flex items-center justify-center transition-all"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-[#EC008C]" : "w-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
            <button
              data-testid="testimonial-next"
              onClick={next}
              className="w-10 h-10 rounded-full border border-white/15 hover:border-[#EC008C] hover:bg-[#EC008C]/10 flex items-center justify-center transition-all"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
